import React, { useEffect, useMemo, useState } from "react";
import { api, formatMXN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, ShoppingCart, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

export default function Insumos() {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cotId, setCotId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/cotizaciones");
      setCotizaciones(data);
    })();
  }, []);

  useEffect(() => {
    if (!cotId) { setData(null); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/cotizaciones/${cotId}/insumos`);
        setData(data);
      } catch {
        toast.error("Error al obtener insumos");
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [cotId]);

  const grouped = useMemo(() => {
    if (!data?.items) return {};
    const g = {};
    for (const it of data.items) {
      const k = it.categoria_material || "OTROS";
      if (!g[k]) g[k] = [];
      g[k].push(it);
    }
    return g;
  }, [data]);

  const catOrder = ["MATERIA PRIMA", "HERRAJES", "INSUMOS", "CUBIERTAS", "BARNIZ", "OTROS"];

  const exportCSV = () => {
    if (!data?.items?.length) return;
    const rows = [["Categoría", "Material", "Unidad", "Cantidad total", "Precio unitario (MXN)", "Importe (MXN)", "Importe c/IVA (MXN)"]];
    for (const it of data.items) {
      rows.push([
        it.categoria_material,
        it.nombre,
        it.unidad,
        it.cantidad_total,
        it.precio_unitario,
        it.importe,
        it.importe_con_iva,
      ]);
    }
    rows.push([]);
    rows.push(["", "", "", "", "Subtotal", data.subtotal, ""]);
    rows.push(["", "", "", "", "IVA 16%", data.iva, ""]);
    rows.push(["", "", "", "", "TOTAL", data.total, ""]);
    const csv = rows.map((r) => r.map((c) => (typeof c === "string" && c.includes(",") ? `"${c}"` : String(c))).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `insumos-${data.folio || "cotizacion"}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-8" data-testid="insumos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 no-print">
        <div>
          <div className="uppercase-label">Compras</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <ShoppingCart className="w-9 h-9 text-[#8B5A33]" strokeWidth={1.5} />
            Insumos
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Consolida los materiales de todas las partidas de una cotización para pasar la lista
            directo a compras. Se suman cantidades cuando el mismo material se repite y se calcula
            el importe con IVA.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data?.items?.length > 0 && (
            <>
              <Button variant="outline" onClick={exportCSV} data-testid="insumos-export">
                <Download className="w-4 h-4 mr-2" /> Exportar CSV
              </Button>
              <Button variant="outline" onClick={() => window.print()} data-testid="insumos-print">
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="no-print bg-white border border-border rounded-lg p-5">
        <label className="uppercase-label block mb-2">Selecciona la cotización</label>
        <Select value={cotId || ""} onValueChange={setCotId}>
          <SelectTrigger className="w-full max-w-2xl" data-testid="insumos-select-cot">
            <SelectValue placeholder="Elige una cotización para consolidar sus insumos" />
          </SelectTrigger>
          <SelectContent>
            {cotizaciones.filter((c) => c && c.id).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.folio} · {c.cliente_nombre} · {c.proyecto}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="text-muted-foreground">Calculando insumos…</div>}

      {data && !loading && (
        <div className="print-area">
          <div className="paper-sheet rounded p-6 md:p-10 space-y-6">
            <div className="flex flex-wrap justify-between items-end gap-4 border-b border-border pb-4">
              <div>
                <div className="uppercase-label">Lista de insumos para compras</div>
                <h2 className="font-display text-3xl mt-1">{data.folio}</h2>
                <div className="text-sm text-foreground/80 mt-1">
                  <span className="font-medium">{data.cliente_nombre}</span> · {data.proyecto}
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {data.items.length} materiales · {new Date().toLocaleDateString("es-MX")}
              </div>
            </div>

            {data.partidas_sin_desglose?.length > 0 && (
              <div className="border border-[#D4A373]/40 bg-[#D4A373]/10 rounded p-3 text-xs" data-testid="insumos-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#8B5A33] mt-0.5" />
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-[11px]">Partidas sin desglose de materiales</div>
                    <ul className="mt-1 space-y-0.5">
                      {data.partidas_sin_desglose.map((p, i) => (
                        <li key={i}>· <span className="tabular font-medium">{p.codigo}</span> — {p.motivo}</li>
                      ))}
                    </ul>
                    <div className="mt-1 text-muted-foreground">Estas partidas no aparecen en la lista. Configura sus materiales en el catálogo para incluirlas.</div>
                  </div>
                </div>
              </div>
            )}

            {data.items.length === 0 && (
              <div className="text-center py-16 text-muted-foreground text-sm">
                Esta cotización no tiene materiales consolidables (ninguna partida tiene desglose configurado).
              </div>
            )}

            {catOrder.filter((c) => grouped[c]?.length).concat(Object.keys(grouped).filter((c) => !catOrder.includes(c))).map((cat) => {
              const rows = grouped[cat] || [];
              if (rows.length === 0) return null;
              const catSubtotal = rows.reduce((s, r) => s + r.importe, 0);
              return (
                <div key={cat} data-testid={`insumos-cat-${cat}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-xl text-[#2f3d31]">{cat}</div>
                    <div className="text-xs text-muted-foreground tabular">{rows.length} ítems · {formatMXN(catSubtotal)}</div>
                  </div>
                  <div className="border border-border rounded overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead className="bg-[#F4F2EC]">
                        <tr>
                          <th className="px-3 py-2 text-left uppercase-label">Material</th>
                          <th className="px-3 py-2 text-left uppercase-label w-20">Unidad</th>
                          <th className="px-3 py-2 text-right uppercase-label w-28">Cantidad</th>
                          <th className="px-3 py-2 text-right uppercase-label w-32">P.U.</th>
                          <th className="px-3 py-2 text-right uppercase-label w-32">Importe</th>
                          <th className="px-3 py-2 text-right uppercase-label w-36">Importe c/IVA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((it, i) => (
                          <tr key={`${cat}-${it.nombre}-${i}`} className="border-t border-border" data-testid={`insumo-row-${it.nombre}`}>
                            <td className="px-3 py-2 font-medium">{it.nombre}</td>
                            <td className="px-3 py-2 text-muted-foreground">{it.unidad}</td>
                            <td className="px-3 py-2 text-right tabular">{it.cantidad_total.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right tabular">{formatMXN(it.precio_unitario)}</td>
                            <td className="px-3 py-2 text-right tabular">{formatMXN(it.importe)}</td>
                            <td className="px-3 py-2 text-right tabular font-medium">{formatMXN(it.importe_con_iva)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {data.items.length > 0 && (
              <div className="totals-block border border-border rounded overflow-hidden max-w-md ml-auto">
                <table className="w-full text-[12px]">
                  <tbody>
                    <tr>
                      <td className="px-3 py-2 text-right uppercase font-bold bg-[#bbbbb4]">SUBTOTAL</td>
                      <td className="px-3 py-2 text-right tabular bg-[#bbbbb4] font-bold w-36" data-testid="insumos-subtotal">{formatMXN(data.subtotal)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-right uppercase font-bold bg-[#999991]">IVA 16%</td>
                      <td className="px-3 py-2 text-right tabular bg-[#999991] font-bold" data-testid="insumos-iva">{formatMXN(data.iva)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-right uppercase font-bold bg-[#2C2A28] text-white">TOTAL</td>
                      <td className="px-3 py-2 text-right tabular bg-[#2C2A28] text-white font-bold text-[14px]" data-testid="insumos-total">{formatMXN(data.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
